import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Printer, ArrowsClockwise, FloppyDisk } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import TimeStudyLibrary from "@/components/TimeStudyLibrary";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const LS_KEY = "ops_time_study_draft_v1";
const SAMPLE = `07:00-07:15 CH Charla Seguridad
07:15-08:30 OP Rezagado 1h15m
08:30-09:00 OP Retro Amacizado 0h30m
09:00-11:30 OP Anclador - riego, adaptación, instalación reebar
11:30-12:00 AL Comida
12:00-12:45 FA Falla manguera hidráulica
12:45-15:30 OP Pruebas barrenar
15:30-15:45 SR Transición de frente
15:45-18:15 OP Split Set y Enmallado 2h30m
18:15-19:00 DE Espera de bomba Wilden - quedó para barrenar`;

// Catálogo de códigos: HH:MM-HH:MM <CÓDIGO> actividad  (el código es opcional; sin código se infiere por palabras clave)
export const CODES = [
  { code: "OP", type: "OPERACION", label: "Operación", productive: true, bar: "bg-black text-white", tag: "bg-black text-white", sw: "bg-black" },
  { code: "SR", type: "S/R", label: "Standby / Transición", bar: "bg-white text-black border-2 border-dashed border-black", tag: "border border-dashed border-black text-black", sw: "border-2 border-dashed border-black bg-white" },
  { code: "CH", type: "CHARLA", label: "Charla / Capacitación", bar: "bg-white text-black border-[3px] border-double border-black", tag: "border-[3px] border-double border-black text-black", sw: "border-[3px] border-double border-black bg-white" },
  { code: "FA", type: "FALLA", label: "Falla mecánica", bar: "text-white border border-black ts-hatch", tag: "ts-hatch text-white", sw: "ts-hatch" },
  { code: "DE", type: "DEMORA", label: "Demora operativa", bar: "bg-slate-400 text-black border border-black", tag: "bg-slate-400 text-black", sw: "bg-slate-400" },
  { code: "AL", type: "ALIMENTOS", label: "Alimentos / Descanso", bar: "bg-white text-black border border-black ts-dots", tag: "border border-black ts-dots text-black", sw: "border border-black ts-dots bg-white" },
  { code: "MT", type: "MANTTO", label: "Mantenimiento programado", bar: "text-black border border-black ts-hatch-light", tag: "ts-hatch-light text-black border border-black", sw: "ts-hatch-light border border-black" },
];
const BY_CODE = Object.fromEntries(CODES.map((c) => [c.code, c]));
export const BY_TYPE = Object.fromEntries(CODES.map((c) => [c.type, c]));
const CODE_RE = new RegExp(`^\\[?(${CODES.map((c) => c.code).join("|")})\\]?[:\\-]?\\s+(.*)$`, "i");

const toMin = (s) => { const m = /^(\d{1,2}):(\d{2})$/.exec((s || "").trim()); return m ? +m[1] * 60 + +m[2] : null; };
const fmtMin = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const fmtDur = (m) => `${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}m`;
const fmtDate = (iso) => {
  if (!iso) return "";
  const [y, mo, d] = iso.split("-").map(Number);
  const months = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
  return `${d} ${months[mo - 1]} ${y}`;
};

export const classify = (text) => {
  const t = text.toLowerCase();
  if (/falla|aver[ií]a|descompost/.test(t)) return "FALLA";
  if (/mantenimiento|mantto|servicio preventivo/.test(t)) return "MANTTO";
  if (/charla|platica|plática|capacitaci/.test(t)) return "CHARLA";
  if (/comida|alimento|descanso|lonche|almuerzo/.test(t)) return "ALIMENTOS";
  if (/demora|espera|sin material|sin energ|sin agua|sin aire/.test(t)) return "DEMORA";
  if (/s\/r|standby|stand by|transici[oó]n|punteado|traslado/.test(t)) return "S/R";
  return "OPERACION";
};

export const parseShift = (shift) => {
  const m = /^(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})$/.exec((shift || "").trim());
  if (!m) return null;
  const start = toMin(m[1]); let end = toMin(m[2]);
  if (start == null || end == null) return null;
  if (end <= start) end += 1440;
  return { start, end, total: end - start };
};

export const parseLines = (text, shift) => {
  const out = [];
  for (const raw of (text || "").split("\n")) {
    const line = raw.trim();
    if (!line) continue;
    const m = /^(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})\s+(.*)$/.exec(line);
    if (!m) { out.push({ error: true, raw: line }); continue; }
    let s = toMin(m[1]); let e = toMin(m[2]);
    if (shift && s < shift.start) s += 1440;
    if (shift && e < shift.start) e += 1440;
    if (e < s) e += 1440;
    let name = m[3].trim(); let type; let explicit = false;
    const cm = CODE_RE.exec(name);
    if (cm) { type = BY_CODE[cm[1].toUpperCase()].type; name = cm[2].trim(); explicit = true; }
    else type = classify(name);
    out.push({ start: s, end: e, dur: e - s, name, type, code: BY_TYPE[type].code, explicit, raw: line });
  }
  return out;
};


export default function OperationsTimeStudy({ canWrite = false }) {
  const [company, setCompany] = useState({ name: "", logo: null });
  const [savedId, setSavedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [libKey, setLibKey] = useState(0);
  const [form, setForm] = useState(() => {
    try { const d = JSON.parse(localStorage.getItem(LS_KEY) || "null"); if (d) return d; } catch { /* ignore */ }
    return { date: new Date().toISOString().slice(0, 10), shift: "07:00-19:00", supervisor: "", area: "", operator: "", text: SAMPLE };
  });
  const [applied, setApplied] = useState(form);

  useEffect(() => { api.get("/settings/company").then((r) => setCompany(r.data)).catch(() => {}); }, []);
  useEffect(() => { localStorage.setItem(LS_KEY, JSON.stringify(form)); }, [form]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const shift = useMemo(() => parseShift(applied.shift), [applied.shift]);
  const acts = useMemo(() => parseLines(applied.text, shift), [applied.text, shift]);
  const valid = acts.filter((a) => !a.error);
  const errors = acts.filter((a) => a.error);
  const hours = useMemo(() => {
    if (!shift) return [];
    const h = []; for (let m = shift.start; m < shift.end; m += 60) h.push(m % 1440); return h;
  }, [shift]);

  const save = async () => {
    const live = parseLines(form.text, parseShift(form.shift)).filter((a) => !a.error);
    if (!live.length) return toast.error("No hay actividades válidas para guardar");
    setApplied(form); setSaving(true);
    const payload = { ...form, activities: live.map(({ start, end, dur, name, type }) => ({ start, end, dur, name, type })) };
    try {
      const r = savedId ? await api.put(`/operations/time-studies/${savedId}`, payload) : await api.post("/operations/time-studies", payload);
      setSavedId(r.data.id); setLibKey((k) => k + 1);
      toast.success(savedId ? "Estudio actualizado" : "Estudio guardado");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const loadStudy = (s) => {
    const f = { date: s.date, shift: s.shift, supervisor: s.supervisor || "", area: s.area || "", operator: s.operator || "", text: s.text || "" };
    setForm(f); setApplied(f); setSavedId(s.id); toast.success("Estudio cargado");
  };
  const printStudy = (s) => {
    const f = { date: s.date, shift: s.shift, supervisor: s.supervisor || "", area: s.area || "", operator: s.operator || "", text: s.text || "" };
    setForm(f); setApplied(f); setSavedId(s.id);
    toast.success("Preparando impresión…");
    setTimeout(() => window.print(), 300);
  };
  const insertCode = (code) => {
    const last = valid[valid.length - 1];
    const startM = last ? last.end % 1440 : (shift ? shift.start : 420);
    const line = `${fmtMin(startM)}-${fmtMin((startM + 30) % 1440)} ${code} `;
    setForm((f) => ({ ...f, text: (f.text.trimEnd() ? f.text.trimEnd() + "\n" : "") + line }));
    setTimeout(() => { const ta = document.querySelector('[data-testid="ts-lines"]'); if (ta) { ta.focus(); ta.selectionStart = ta.selectionEnd = ta.value.length; } }, 0);
  };
  const newStudy = () => { setSavedId(null); toast.info("Nuevo estudio (el siguiente guardado crea uno nuevo)"); };

  const pct = (m) => shift ? Math.max(0, Math.min(100, ((m - shift.start) / shift.total) * 100)) : 0;
  const shiftLabel = shift ? `${fmtMin(shift.start % 1440)}-${fmtMin(shift.end % 1440)} (${(shift.total / 60).toFixed(shift.total % 60 ? 1 : 0)}h)` : "—";

  return (
    <div className="space-y-4" data-testid="ops-time-study">
    <div className="grid grid-cols-1 xl:grid-cols-[360px_1fr] gap-4">
      <style>{`
        .ts-hatch{background:repeating-linear-gradient(135deg,#000 0 6px,#fff 6px 10px);}
        .ts-hatch-light{background:repeating-linear-gradient(45deg,#94a3b8 0 3px,#fff 3px 8px);}
        .ts-dots{background:radial-gradient(#000 1.2px,transparent 1.3px) 0 0/6px 6px;}
        @media print{ body *{visibility:hidden} #ts-print,#ts-print *{visibility:visible} #ts-print{position:absolute;left:0;top:0;width:100%;padding:8mm} @page{size:landscape;margin:8mm} }
      `}</style>

      <aside className="bg-white border rounded-lg p-4 space-y-3 print:hidden">
        <div className="flex items-center gap-3 pb-3 border-b-2 border-red-600">
          {company.logo && <img src={company.logo} alt="logo" className="h-14 w-14 object-contain" />}
          <div>
            <p className="font-black text-sm leading-tight uppercase">Generador<br />Estudio de Tiempos</p>
            <p className="text-[11px] text-slate-500">{company.name}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-[11px] uppercase">Fecha</Label><Input type="date" value={form.date} onChange={set("date")} data-testid="ts-date" /></div>
          <div><Label className="text-[11px] uppercase">Turno (inicio-fin)</Label><Input value={form.shift} onChange={set("shift")} placeholder="07:00-19:00" className="font-mono" data-testid="ts-shift" /></div>
          <div><Label className="text-[11px] uppercase">Supervisor</Label><Input value={form.supervisor} onChange={set("supervisor")} placeholder="Nombre" data-testid="ts-supervisor" /></div>
          <div><Label className="text-[11px] uppercase">Frente / Área</Label><Input value={form.area} onChange={set("area")} placeholder="Ej. Rampa Sur" data-testid="ts-area" /></div>
        </div>
        <div><Label className="text-[11px] uppercase">Operador / Anclador</Label><Input value={form.operator} onChange={set("operator")} placeholder="Nombre operador" data-testid="ts-operator" /></div>
        <div className="text-[11px] bg-amber-50 border border-amber-300 rounded p-2 text-amber-900">
          Formato: <b>HH:MM-HH:MM CÓDIGO Actividad</b>. Ej. <code className="bg-white px-1">12:00-12:45 FA Falla manguera</code>. Si omites el código se infiere por palabras clave.
        </div>
        <div data-testid="ts-codes">
          <Label className="text-[11px] uppercase">Códigos de actividad (clic para insertar)</Label>
          <div className="grid grid-cols-1 gap-1 mt-1">
            {CODES.map((c) => (
              <button key={c.code} type="button" onClick={() => insertCode(c.code)} data-testid={`ts-code-${c.code}`}
                      className="flex items-center gap-2 text-left text-[11px] px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 hover:border-black transition-colors">
                <i className={`inline-block w-6 h-3.5 shrink-0 ${c.sw}`} />
                <span className="font-mono font-black w-7">{c.code}</span>
                <span className="flex-1">{c.label}</span>
                <span className={`text-[9px] font-bold px-1 rounded ${c.productive ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}`}>{c.productive ? "PRODUCTIVO" : "NO PROD."}</span>
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-[11px] uppercase">Pega líneas (HH:MM-HH:MM código actividad)</Label>
          <Textarea rows={12} value={form.text} onChange={set("text")} className="font-mono text-xs" data-testid="ts-lines" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Button className="bg-black hover:bg-slate-800 text-white" onClick={() => setApplied(form)} data-testid="ts-update">
            <ArrowsClockwise size={14} weight="bold" className="mr-1" /> Actualizar
          </Button>
          <Button className="bg-red-700 hover:bg-red-800 text-white" onClick={() => { setApplied(form); setTimeout(() => window.print(), 150); }} data-testid="ts-print">
            <Printer size={14} weight="bold" className="mr-1" /> Imprimir / PDF
          </Button>
        </div>
        {canWrite && (
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <Button variant="outline" className="border-black" disabled={saving} onClick={save} data-testid="ts-save">
              <FloppyDisk size={14} weight="bold" className="mr-1" /> {savedId ? "Actualizar estudio guardado" : "Guardar estudio"}
            </Button>
            {savedId && <Button variant="ghost" className="text-xs" onClick={newStudy} data-testid="ts-new">Nuevo</Button>}
          </div>
        )}
        <p className="text-[11px] font-mono bg-slate-100 rounded p-2" data-testid="ts-status">
          Gantt: {shiftLabel} = {shift ? shift.total : 0}min | {valid.length} acts{errors.length ? ` | ${errors.length} línea(s) inválida(s)` : " | OK"}
        </p>
        {errors.length > 0 && (
          <ul className="text-[11px] text-red-700 list-disc pl-4">{errors.map((e, i) => <li key={i}>{e.raw}</li>)}</ul>
        )}
      </aside>

      <section id="ts-print" className="bg-white border rounded-lg p-4 text-black">
        <div className="flex items-center gap-4 pb-2 border-b-[3px] border-red-600">
          {company.logo && <img src={company.logo} alt="logo" className="h-16 w-16 object-contain" />}
          <div className="flex-1 text-center">
            <h2 className="font-black text-xl tracking-wide uppercase">Estudio de Tiempos - Ciclo Completo</h2>
            <p className="text-xs font-semibold">{company.name}</p>
            <p className="inline-block text-[11px] border rounded px-2 py-0.5 mt-1 bg-slate-50">
              Turno: {shiftLabel} | Fecha: {fmtDate(applied.date)}
              {applied.area && ` | Frente: ${applied.area}`}{applied.operator && ` | Operador: ${applied.operator}`}
            </p>
          </div>
        </div>

        <div className="mt-3 border border-black">
          <div className="bg-black text-white text-xs font-bold px-2 py-1 uppercase">Diagrama de Gantt - {shiftLabel} - Conectado al turno</div>
          <div className="grid" style={{ gridTemplateColumns: "200px 1fr" }}>
            <div className="text-[10px] font-bold px-2 py-1 border-b border-r border-black bg-slate-50">ACTIVIDAD</div>
            <div className="relative border-b border-black bg-slate-50 grid" style={{ gridTemplateColumns: `repeat(${hours.length || 1},1fr)` }}>
              {hours.map((h) => <div key={h} className="text-[10px] font-bold text-center py-1 border-l border-slate-300">{fmtMin(h)}</div>)}
            </div>
            {valid.map((a, i) => (
              <div key={i} className="contents">
                <div className="text-[10px] font-semibold px-2 py-2 border-b border-r border-black leading-tight flex items-start gap-1.5">
                  <span className={`shrink-0 font-mono font-black text-[9px] px-1 ${BY_TYPE[a.type].tag}`}>{a.code}</span>
                  <span>{i + 1}. {a.name}</span>
                </div>
                <div className="relative border-b border-black" style={{ minHeight: 36 }}>
                  <div className="absolute inset-0 grid" style={{ gridTemplateColumns: `repeat(${hours.length || 1},1fr)` }}>
                    {hours.map((h) => <div key={h} className="border-l border-dashed border-slate-300" />)}
                  </div>
                  <div className={`absolute top-1.5 bottom-1.5 flex items-center justify-center text-[10px] font-bold overflow-hidden whitespace-nowrap ${BY_TYPE[a.type].bar}`}
                       style={{ left: `${pct(a.start)}%`, width: `${Math.max(0.6, pct(a.end) - pct(a.start))}%` }}
                       title={`${a.name} ${fmtDur(a.dur)}`} data-testid={`ts-bar-${i}`}>
                    <span className={["FALLA", "MANTTO", "ALIMENTOS"].includes(a.type) ? (a.type === "FALLA" ? "bg-black px-1" : "bg-white px-1") : ""}>{a.code} · {fmtDur(a.dur)}</span>
                  </div>
                </div>
              </div>
            ))}
            {valid.length === 0 && <div className="col-span-2 text-center text-xs py-6 text-slate-500">Sin actividades válidas.</div>}
          </div>
          <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-[10px] px-2 py-1 border-t border-black bg-slate-50" data-testid="ts-legend">
            {CODES.map((c) => (
              <span key={c.code} className="flex items-center gap-1"><i className={`inline-block w-5 h-3 ${c.sw}`} /><b className="font-mono">{c.code}</b> {c.label}</span>
            ))}
            <span className="ml-auto">Conectado a {shiftLabel}</span>
          </div>
        </div>

        {valid.length > 0 && (
          <div className="mt-3 grid grid-cols-4 md:grid-cols-8 gap-1.5" data-testid="ts-code-summary">
            {CODES.map((c) => {
              const mins = valid.filter((a) => a.type === c.type).reduce((s, a) => s + a.dur, 0);
              const tot = valid.reduce((s, a) => s + a.dur, 0) || 1;
              return (
                <div key={c.code} className={`border border-black p-1.5 ${mins ? "" : "opacity-40"}`} data-testid={`ts-sum-${c.code}`}>
                  <div className="flex items-center gap-1"><i className={`inline-block w-4 h-2.5 ${c.sw}`} /><b className="font-mono text-[10px]">{c.code}</b></div>
                  <div className="text-xs font-black leading-tight">{fmtDur(mins)}</div>
                  <div className="text-[9px] text-slate-600">{Math.round(mins / tot * 100)}%</div>
                </div>
              );
            })}
            <div className="border-2 border-black p-1.5 bg-black text-white" data-testid="ts-sum-util">
              <div className="text-[9px] uppercase font-bold">Utilización</div>
              <div className="text-sm font-black leading-tight">
                {Math.round(valid.filter((a) => BY_TYPE[a.type].productive).reduce((s, a) => s + a.dur, 0) / (valid.reduce((s, a) => s + a.dur, 0) || 1) * 100)}%
              </div>
              <div className="text-[9px]">productivo / total</div>
            </div>
          </div>
        )}

        <div className="mt-3 border border-black">
          <div className="bg-black text-white text-xs font-bold px-2 py-1 uppercase">Tabla de datos - Detalle de ciclo</div>
          <table className="w-full text-[10px]" data-testid="ts-table">
            <thead className="bg-black text-white">
              <tr>{["#", "CÓD.", "ACTIVIDAD", "DURACIÓN", "INICIO", "FIN", "TIPO", "OBSERVACIONES"].map((h) => <th key={h} className="px-2 py-1 text-left font-bold">{h}</th>)}</tr>
            </thead>
            <tbody>
              {valid.map((a, i) => (
                <tr key={i} className="border-b border-slate-300 odd:bg-white even:bg-slate-50">
                  <td className="px-2 py-1">{i + 1}</td>
                  <td className="px-2 py-1"><span className={`inline-block font-mono font-black text-[9px] px-1 ${BY_TYPE[a.type].tag}`} title={a.explicit ? "Código explícito" : "Inferido por palabras clave"}>{a.code}{!a.explicit && "*"}</span></td>
                  <td className="px-2 py-1 font-semibold">{a.name}</td>
                  <td className="px-2 py-1 whitespace-nowrap">{fmtDur(a.dur)}</td>
                  <td className="px-2 py-1">{fmtMin(a.start % 1440)}</td>
                  <td className="px-2 py-1">{fmtMin(a.end % 1440)}</td>
                  <td className="px-2 py-1"><span className={`inline-block px-1.5 py-0.5 text-[9px] font-bold ${BY_TYPE[a.type].tag}`}>{BY_TYPE[a.type].label}</span></td>
                  <td className="px-2 py-1 text-slate-700">{a.raw}</td>
                </tr>
              ))}
              {valid.length > 0 && (
                <tr className="font-bold bg-slate-100">
                  <td className="px-2 py-1" colSpan={3}>TOTAL ({valid.length} actividades) · * = código inferido</td>
                  <td className="px-2 py-1">{fmtDur(valid.reduce((s, a) => s + a.dur, 0))}</td>
                  <td className="px-2 py-1" colSpan={4}>
                    Productivo {fmtDur(valid.filter((a) => BY_TYPE[a.type].productive).reduce((s, a) => s + a.dur, 0))} ·
                    No productivo {fmtDur(valid.filter((a) => !BY_TYPE[a.type].productive).reduce((s, a) => s + a.dur, 0))}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="text-[9px] text-slate-500 mt-2 flex justify-between">
          <span>{company.name} | {fmtDate(applied.date)} | Turno {applied.shift} | Sup: {applied.supervisor}</span>
          <span>Estudio de tiempos · Landscape</span>
        </p>
      </section>
    </div>
    <TimeStudyLibrary canWrite={canWrite} onLoad={loadStudy} onPrint={printStudy} refreshKey={libKey} />
    </div>
  );
}
